import { useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Upload, X, FileText, CheckCircle2 } from 'lucide-react';
import type { RequestType, Priority } from '@/types';

interface RequestSubmissionPanelProps {
  onSubmit?: (data: RequestFormData) => void;
}

export interface RequestFormData {
  title: string;
  description: string;
  requestType: RequestType;
  priority: Priority;
  files: File[];
}

export function RequestSubmissionPanel({ onSubmit }: RequestSubmissionPanelProps) {
  const [formData, setFormData] = useState<RequestFormData>({
    title: '',
    description: '',
    requestType: 'new-task',
    priority: 'standard',
    files: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const requestTypes: { value: RequestType; label: string; icon: string; description: string }[] = [
    { value: 'new-task', label: 'New Task', icon: '📝', description: 'Request a new development task' },
    { value: 'bug-fix', label: 'Bug Fix', icon: '🐛', description: 'Report and fix a bug' },
    { value: 'landing-page', label: 'Landing Page', icon: '🚀', description: 'Build a new landing page' },
    { value: 'small-change', label: 'Small Change', icon: '✏️', description: 'Quick text or style updates' },
  ];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    onSubmit?.(formData);
    setIsSubmitting(false);
    setShowSuccess(true);
    
    setTimeout(() => {
      setShowSuccess(false);
      setFormData({
        title: '',
        description: '',
        requestType: 'new-task',
        priority: 'standard',
        files: [],
      });
    }, 3000);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFormData((prev) => ({
        ...prev,
        files: [...prev.files, ...Array.from(e.target.files!)],
      }));
    }
  };

  const removeFile = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  if (showSuccess) {
    return (
      <Card className="text-center py-12">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold text-slate-900 mb-2">Request Submitted!</h3>
        <p className="text-slate-500 mb-6">Your request has been received. We'll review it shortly.</p>
        <div className="text-sm text-slate-400">
          Request ID: #{Math.random().toString(36).substr(2, 9).toUpperCase()}
        </div>
      </Card>
    );
  }

  return (
    <Card title="Submit New Request" subtitle="Create a new task, bug report, or change request">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Request Type Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Request Type
          </label>
          <div className="grid grid-cols-2 gap-3">
            {requestTypes.map((type) => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, requestType: type.value }))}
                className={`p-4 rounded-lg border-2 text-left transition-all ${
                  formData.requestType === type.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="text-2xl mb-2">{type.icon}</div>
                <div className="font-medium text-slate-900">{type.label}</div>
                <div className="text-xs text-slate-500 mt-1">{type.description}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Priority Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Priority
          </label>
          <div className="flex gap-3">
            {(['standard', 'urgent'] as Priority[]).map((priority) => (
              <button
                key={priority}
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, priority }))}
                className={`flex-1 py-3 px-4 rounded-lg border-2 font-medium transition-all ${
                  formData.priority === priority
                    ? priority === 'urgent'
                      ? 'border-red-500 bg-red-50 text-red-700'
                      : 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
              >
                {priority === 'urgent' && '🔥 '}
                {priority.charAt(0).toUpperCase() + priority.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Title */}
        <Input
          label="Title"
          placeholder="What needs to be done?"
          value={formData.title}
          onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
          error={errors.title}
        />

        {/* Description */}
        <Textarea
          label="Description"
          placeholder="Provide detailed requirements, context, and any specific instructions..."
          value={formData.description}
          onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
          error={errors.description}
          rows={5}
        />

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-3">
            Attachments
          </label>
          <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:bg-slate-50 transition-colors">
            <input
              type="file"
              multiple
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-600 font-medium">Click to upload files</p>
              <p className="text-xs text-slate-400 mt-1">Support for images, documents, and design files</p>
            </label>
          </div>
          
          {/* File list */}
          {formData.files.length > 0 && (
            <div className="mt-3 space-y-2">
              {formData.files.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 bg-slate-50 rounded-lg"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-700">{file.name}</span>
                    <span className="text-xs text-slate-400">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Submit */}
        <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
          <Button
            type="submit"
            isLoading={isSubmitting}
            className="flex-1"
          >
            Submit Request
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setFormData({
                title: '',
                description: '',
                requestType: 'new-task',
                priority: 'standard',
                files: [],
              })
            }
          >
            Clear
          </Button>
        </div>
      </form>
    </Card>
  );
}
